import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  deleteDoc,
  doc,
  getDoc,
  getFirestore,
  Timestamp,
  updateDoc
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBuilding,
  faCalendarDay,
  faEnvelope,
  faIdCard,
  faMoneyCheckDollar,
  faPenToSquare,
  faPhone,
  faShieldHalved,
  faTrash,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { app } from '../../firebase';
import '../dash.css';
import {
  formatDateTime,
  formatShortDate,
  getDateTimeInputValue
} from '../../utils/financeWorkflow';
import { logAdminActivity } from '../../utils/activityLogger';

const LOTUS_API_BASE_URL = process.env.REACT_APP_LOTUS_API_BASE_URL
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/lotusApi';

const PAYMENT_METHOD_OPTIONS = ['Bank Transfer', 'MPESA', 'Paybill', 'Till Number', 'Cash', 'Cheque', 'Other'];
const VENDOR_STATUS_OPTIONS = ['Active', 'Inactive'];

const normalizeVendorStatus = (status) => String(status || 'Active').trim() || 'Active';

const normalizeVendorPaymentMethod = (method) => {
  const normalized = String(method || '').trim();
  if (!normalized) return PAYMENT_METHOD_OPTIONS[0];
  return PAYMENT_METHOD_OPTIONS.includes(normalized) ? normalized : normalized;
};

const trimValue = (value) => String(value || '').trim();

const buildVendorForm = (vendor = {}) => ({
  branch: vendor.branch || 'General',
  vendorName: vendor.vendorName || '',
  vendorServices: vendor.vendorServices || '',
  vendorContactPerson: vendor.vendorContactPerson || '',
  vendorEmail: vendor.vendorEmail || '',
  vendorPhone: vendor.vendorPhone || '',
  vendorStatus: normalizeVendorStatus(vendor.vendorStatus || vendor.status),
  serviceDesc: vendor.serviceDesc || '',
  commDate: getDateTimeInputValue(vendor.commDate || vendor.timeCreated || new Date()),
  vendorPaymentMethod: normalizeVendorPaymentMethod(vendor.vendorPaymentMethod || vendor.paymentMethod),
  vendorPaymentBankId: vendor.vendorPaymentBankId || '',
  vendorPaymentBankName: vendor.vendorPaymentBankName || '',
  vendorPaymentBankCode: vendor.vendorPaymentBankCode || '',
  vendorPaymentReference: vendor.vendorPaymentReference || '',
  vendorPaymentPhone: vendor.vendorPaymentPhone || '',
  vendorPaymentPaybillNumber: vendor.vendorPaymentPaybillNumber || '',
  vendorPaymentPaybillAccountNumber: vendor.vendorPaymentPaybillAccountNumber || '',
  vendorPaymentTillNumber: vendor.vendorPaymentTillNumber || '',
  paymentDetails: vendor.paymentDetails || ''
});

const mapLotusBankOptions = (payload) => {
  const rawBanks = payload?.data?.banks || payload?.data?.bank_list || payload?.banks || [];
  if (!Array.isArray(rawBanks)) return [];

  return rawBanks
    .map((bank) => {
      const bankId = String(bank?.id || bank?.bank_id || bank?.bankId || bank?.code || bank?.bank_code || '').trim();
      const bankName = String(
        bank?.name || bank?.bank_name || bank?.bankName || bank?.display_name || bank?.label || bankId || 'Bank'
      ).trim();

      return {
        id: bankId || bankName,
        name: bankName,
        code: String(bank?.code || bank?.bank_code || bank?.bankCode || '').trim()
      };
    })
    .filter((bank) => bank.id || bank.name);
};

const getVendorPayoutSummary = (vendor = {}) => {
  const method = normalizeVendorPaymentMethod(vendor.vendorPaymentMethod || vendor.paymentMethod);

  if (method === 'Bank Transfer') {
    return {
      method,
      detail: [
        vendor.vendorPaymentBankName || 'Bank not set',
        vendor.vendorPaymentReference || 'Account number not set'
      ].filter(Boolean).join(' • '),
      ready: Boolean(String(vendor.vendorPaymentBankId || '').trim() && String(vendor.vendorPaymentReference || '').trim())
    };
  }

  if (method === 'MPESA') {
    return {
      method,
      detail: vendor.vendorPaymentPhone || 'Phone not set',
      ready: Boolean(String(vendor.vendorPaymentPhone || '').trim())
    };
  }

  if (method === 'Paybill') {
    return {
      method,
      detail: [
        vendor.vendorPaymentPaybillNumber || 'Paybill not set',
        vendor.vendorPaymentPaybillAccountNumber || 'Account not set'
      ].filter(Boolean).join(' • '),
      ready: Boolean(
        String(vendor.vendorPaymentPaybillNumber || '').trim()
        && String(vendor.vendorPaymentPaybillAccountNumber || '').trim()
      )
    };
  }

  if (method === 'Till Number') {
    return {
      method,
      detail: vendor.vendorPaymentTillNumber || 'Till number not set',
      ready: Boolean(String(vendor.vendorPaymentTillNumber || '').trim())
    };
  }

  if (method === 'Cash') {
    return { method, detail: vendor.vendorPaymentReference || 'Cash payout', ready: true };
  }

  if (method === 'Cheque') {
    return { method, detail: vendor.vendorPaymentReference || 'Cheque details not set', ready: true };
  }

  return {
    method: method || 'Not set',
    detail: vendor.vendorPaymentReference || 'Payout method set',
    ready: Boolean(String(method || '').trim())
  };
};

const hasLockedVendorPaymentDetails = (vendor = {}) => {
  if (vendor.vendorPaymentDetailsLocked) {
    return true;
  }

  return Boolean(getVendorPayoutSummary(vendor).ready && trimValue(vendor.vendorPaymentMethod || vendor.paymentMethod));
};

function ViewVendorComp() {
  const { id } = useParams();
  const navigate = useNavigate();
  const firestore = getFirestore(app);
  const auth = getAuth();
  const hasLoggedViewRef = useRef(false);

  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [banksLoaded, setBanksLoaded] = useState(false);
  const [bankOptions, setBankOptions] = useState([]);
  const [bankLoadError, setBankLoadError] = useState('');
  const [formState, setFormState] = useState(buildVendorForm());

  const loadVendor = useCallback(async () => {
    if (!id) {
      setVendor(null);
      return;
    }

    const vendorSnap = await getDoc(doc(firestore, 'Vendors', id));
    if (!vendorSnap.exists()) {
      setVendor(null);
      return;
    }

    setVendor({ id: vendorSnap.id, ...vendorSnap.data() });
  }, [firestore, id]);

  const loadLotusBanks = useCallback(async ({ force = false } = {}) => {
    if ((banksLoaded && !force) || loadingBanks) {
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setBankLoadError('Sign in again to load bank options.');
      return;
    }

    setLoadingBanks(true);
    setBankLoadError('');

    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${LOTUS_API_BASE_URL}/banks`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || payload?.message || 'Failed to load banks.');
      }

      setBankOptions(mapLotusBankOptions(payload));
      setBanksLoaded(true);
    } catch (error) {
      console.error('Failed to load Lotus banks:', error);
      setBankLoadError('Banks could not be loaded right now.');
    } finally {
      setLoadingBanks(false);
    }
  }, [auth, banksLoaded, loadingBanks]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadVendor();
      } catch (error) {
        console.error('Failed to load vendor profile:', error);
        swal('Error', 'Vendor details could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [loadVendor]);

  useEffect(() => {
    if (!vendor) {
      return;
    }

    setFormState(buildVendorForm(vendor));
  }, [vendor]);

  useEffect(() => {
    if (editModalOpen && formState.vendorPaymentMethod === 'Bank Transfer' && !banksLoaded) {
      loadLotusBanks();
    }
  }, [banksLoaded, editModalOpen, formState.vendorPaymentMethod, loadLotusBanks]);

  useEffect(() => {
    if (!vendor || hasLoggedViewRef.current) return;

    logAdminActivity({
      action: 'Viewed Vendor Profile',
      details: `Opened vendor profile for ${vendor.vendorName || 'vendor'}`,
      meta: {
        vendorId: vendor.id,
        vendorName: vendor.vendorName || '',
        vendorSection: 'profile'
      }
    });

    hasLoggedViewRef.current = true;
  }, [vendor]);

  const vendorStatus = normalizeVendorStatus(vendor?.vendorStatus || vendor?.status);
  const payoutSummary = useMemo(() => getVendorPayoutSummary(vendor || {}), [vendor]);
  const vendorPaymentDetailsLocked = useMemo(
    () => hasLockedVendorPaymentDetails(vendor || {}),
    [vendor]
  );

  const infoCards = [
    { icon: faBuilding, label: 'Campus', value: vendor?.branch === 'General' ? 'General' : vendor?.branch || 'Not set' },
    { icon: faUser, label: 'Contact person', value: vendor?.vendorContactPerson || 'Not set' },
    { icon: faEnvelope, label: 'Email', value: vendor?.vendorEmail || 'Not set' },
    { icon: faPhone, label: 'Phone', value: vendor?.vendorPhone || 'Not set' },
    { icon: faCalendarDay, label: 'Start date', value: formatShortDate(vendor?.commDate, 'Not set') },
    { icon: faIdCard, label: 'Service line', value: vendor?.vendorServices || 'Not set' }
  ];

  const managementActions = [
    {
      title: 'View vendor payments',
      description: 'See payment history and settlement records for this vendor.',
      path: `/view-vendor/${id}/payments`
    },
    {
      title: 'Change logs',
      description: 'Review edits, updates, and vendor-related admin activity.',
      path: `/view-vendor/${id}/change-logs`
    },
    {
      title: 'Related bills',
      description: 'Open bills linked to this vendor and follow settlement progress.',
      path: `/view-vendor/${id}/related-bills`
    }
  ];

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    if (vendorPaymentDetailsLocked && (name.startsWith('vendorPayment') || name === 'paymentDetails')) {
      return;
    }

    setFormState((currentForm) => {
      if (name === 'vendorPaymentMethod') {
        return {
          ...currentForm,
          [name]: value,
          vendorPaymentBankId: '',
          vendorPaymentBankName: '',
          vendorPaymentBankCode: '',
          vendorPaymentReference: '',
          vendorPaymentPhone: '',
          vendorPaymentPaybillNumber: '',
          vendorPaymentPaybillAccountNumber: '',
          vendorPaymentTillNumber: ''
        };
      }

      return {
        ...currentForm,
        [name]: value
      };
    });
  };

  const handleBankSelect = (event) => {
    if (vendorPaymentDetailsLocked) {
      return;
    }

    const selectedBankId = event.target.value;
    const selectedBank = bankOptions.find((bank) => bank.id === selectedBankId) || null;

    setFormState((currentForm) => ({
      ...currentForm,
      vendorPaymentBankId: selectedBank?.id || '',
      vendorPaymentBankName: selectedBank?.name || '',
      vendorPaymentBankCode: selectedBank?.code || ''
    }));
  };

  const handleSaveVendor = async () => {
    if (!vendor?.id) return;

    const vendorName = String(formState.vendorName || '').trim();
    const vendorServices = String(formState.vendorServices || '').trim();
    const paymentMethod = vendorPaymentDetailsLocked
      ? normalizeVendorPaymentMethod(vendor.vendorPaymentMethod || vendor.paymentMethod)
      : normalizeVendorPaymentMethod(formState.vendorPaymentMethod);
    const paymentReference = vendorPaymentDetailsLocked
      ? trimValue(vendor.vendorPaymentReference)
      : trimValue(formState.vendorPaymentReference);
    const paymentPhone = vendorPaymentDetailsLocked
      ? trimValue(vendor.vendorPaymentPhone)
      : trimValue(formState.vendorPaymentPhone);
    const paybillNumber = vendorPaymentDetailsLocked
      ? trimValue(vendor.vendorPaymentPaybillNumber)
      : trimValue(formState.vendorPaymentPaybillNumber);
    const paybillAccountNumber = vendorPaymentDetailsLocked
      ? trimValue(vendor.vendorPaymentPaybillAccountNumber)
      : trimValue(formState.vendorPaymentPaybillAccountNumber);
    const tillNumber = vendorPaymentDetailsLocked
      ? trimValue(vendor.vendorPaymentTillNumber)
      : trimValue(formState.vendorPaymentTillNumber);

    if (!vendorName || !vendorServices) {
      swal('Missing details', 'Vendor name and service line are required.', 'warning');
      return;
    }

    if (!vendorPaymentDetailsLocked) {
      if (paymentMethod === 'Bank Transfer' && !trimValue(formState.vendorPaymentBankId)) {
        swal('Missing payout details', 'Select a bank from Lotus for this vendor.', 'warning');
        return;
      }

      if (paymentMethod === 'Bank Transfer' && !paymentReference) {
        swal('Missing payout details', 'Add the vendor bank account number.', 'warning');
        return;
      }

      if (paymentMethod === 'MPESA' && !paymentPhone) {
        swal('Missing payout details', 'Add the vendor MPESA phone number.', 'warning');
        return;
      }

      if (paymentMethod === 'Paybill' && (!paybillNumber || !paybillAccountNumber)) {
        swal('Missing payout details', 'Add the vendor paybill number and paybill account number.', 'warning');
        return;
      }

      if (paymentMethod === 'Till Number' && !tillNumber) {
        swal('Missing payout details', 'Add the vendor till number.', 'warning');
        return;
      }
    }

    setSaving(true);
    try {
      const nextVendorPaymentMethod = vendorPaymentDetailsLocked
        ? normalizeVendorPaymentMethod(vendor.vendorPaymentMethod || vendor.paymentMethod)
        : paymentMethod;
      const nextVendorPaymentBankId = vendorPaymentDetailsLocked
        ? trimValue(vendor.vendorPaymentBankId)
        : trimValue(formState.vendorPaymentBankId);
      const nextVendorPaymentBankName = vendorPaymentDetailsLocked
        ? trimValue(vendor.vendorPaymentBankName)
        : trimValue(formState.vendorPaymentBankName);
      const nextVendorPaymentBankCode = vendorPaymentDetailsLocked
        ? trimValue(vendor.vendorPaymentBankCode)
        : trimValue(formState.vendorPaymentBankCode);
      const nextVendorPaymentReference = vendorPaymentDetailsLocked
        ? trimValue(vendor.vendorPaymentReference)
        : paymentReference;
      const nextVendorPaymentPhone = vendorPaymentDetailsLocked
        ? trimValue(vendor.vendorPaymentPhone)
        : paymentPhone;
      const nextVendorPaymentPaybillNumber = vendorPaymentDetailsLocked
        ? trimValue(vendor.vendorPaymentPaybillNumber)
        : paybillNumber;
      const nextVendorPaymentPaybillAccountNumber = vendorPaymentDetailsLocked
        ? trimValue(vendor.vendorPaymentPaybillAccountNumber)
        : paybillAccountNumber;
      const nextVendorPaymentTillNumber = vendorPaymentDetailsLocked
        ? trimValue(vendor.vendorPaymentTillNumber)
        : tillNumber;
      const nextVendorPaymentDetailsLocked = Boolean(
        vendorPaymentDetailsLocked ||
        getVendorPayoutSummary({
          vendorPaymentMethod: nextVendorPaymentMethod,
          vendorPaymentBankId: nextVendorPaymentBankId,
          vendorPaymentBankName: nextVendorPaymentBankName,
          vendorPaymentReference: nextVendorPaymentReference,
          vendorPaymentPhone: nextVendorPaymentPhone,
          vendorPaymentPaybillNumber: nextVendorPaymentPaybillNumber,
          vendorPaymentPaybillAccountNumber: nextVendorPaymentPaybillAccountNumber,
          vendorPaymentTillNumber: nextVendorPaymentTillNumber
        }).ready
      );

      const payload = {
        branch: formState.branch || 'General',
        vendorName,
        vendorServices,
        vendorContactPerson: formState.vendorContactPerson.trim(),
        vendorEmail: formState.vendorEmail.trim(),
        vendorPhone: formState.vendorPhone.trim(),
        vendorStatus: normalizeVendorStatus(formState.vendorStatus),
        serviceDesc: formState.serviceDesc.trim(),
        commDate: formState.commDate ? new Date(formState.commDate) : null,
        vendorPaymentMethod: nextVendorPaymentMethod,
        vendorPaymentBankId: nextVendorPaymentBankId,
        vendorPaymentBankName: nextVendorPaymentBankName,
        vendorPaymentBankCode: nextVendorPaymentBankCode,
        vendorPaymentReference: nextVendorPaymentReference,
        vendorPaymentPhone: nextVendorPaymentPhone,
        vendorPaymentPaybillNumber: nextVendorPaymentPaybillNumber,
        vendorPaymentPaybillAccountNumber: nextVendorPaymentPaybillAccountNumber,
        vendorPaymentTillNumber: nextVendorPaymentTillNumber,
        vendorPaymentDetailsLocked: nextVendorPaymentDetailsLocked,
        paymentDetails: trimValue(formState.paymentDetails),
        updatedAt: Timestamp.now()
      };

      await updateDoc(doc(firestore, 'Vendors', vendor.id), payload);
      setVendor((currentVendor) => (currentVendor ? { ...currentVendor, ...payload } : currentVendor));

      await logAdminActivity({
        action: 'Updated Vendor',
        details: `Saved vendor details for ${vendorName}`,
        meta: {
          vendorId: vendor.id,
          vendorName,
          vendorSection: 'profile'
        }
      });

      swal('Saved', 'Vendor details updated successfully.', 'success');
      setEditModalOpen(false);
    } catch (error) {
      console.error('Failed to update vendor:', error);
      swal('Error', 'Could not save vendor details right now.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVendor = async () => {
    if (!vendor?.id) return;

    const confirmed = await swal({
      title: 'Delete vendor?',
      text: 'This removes the vendor profile from the directory. Linked payment records will remain for history.',
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true
    });

    if (!confirmed) {
      return;
    }

    setDeleting(true);
    try {
      await deleteDoc(doc(firestore, 'Vendors', vendor.id));

      await logAdminActivity({
        action: 'Deleted Vendor',
        details: `Deleted vendor profile for ${vendor.vendorName || 'vendor'}`,
        meta: {
          vendorId: vendor.id,
          vendorName: vendor.vendorName || '',
          vendorSection: 'profile'
        }
      });

      swal('Deleted', 'Vendor removed successfully.', 'success');
      navigate('/vendors');
    } catch (error) {
      console.error('Failed to delete vendor:', error);
      swal('Error', 'Vendor could not be deleted right now.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="clientsComp applicationsPage">
        <div className="appsTableCard">
          <div className="opsLoadingCard">
            <Spinner animation="border" role="status" />
            <span>Loading vendor...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="clientsComp applicationsPage">
        <div className="appsTableCard">
          <div className="opsEmptyState">
            <h6>Vendor not found</h6>
            <p>The vendor profile you are looking for could not be found.</p>
            <button type="button" className="opsGhostBtn" onClick={() => navigate('/vendors')}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to vendors
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="clientsComp applicationsPage vendorProfilePage">
      <div className="appsHeader">
        <div>
          <button type="button" className="opsGhostBtn mb-3" onClick={() => navigate('/vendors')}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back to vendors
          </button>
          <p className="appsKicker">Vendor management</p>
          <h4>{vendor.vendorName || 'Vendor profile'}</h4>
          <p className="appsSubtext">
            Keep the vendor profile, payment setup, and audit trail in one minimalist workspace.
          </p>
        </div>
        <div className="appsHeaderStats">
          <div className="appsStatChip">
            <span>Status</span>
            <strong>{vendorStatus}</strong>
          </div>
          <div className="appsStatChip">
            <span>Payout method</span>
            <strong>{payoutSummary.method || 'Not set'}</strong>
          </div>
          <div className="appsStatChip">
            <span>Payout ready</span>
            <strong>{payoutSummary.ready ? 'Yes' : 'No'}</strong>
          </div>
        </div>
      </div>

      <div className="appsFiltersCard vendorActionsCard">
        <div className="appsFilterGroup vendorActionsGroup">
          {managementActions.map((action) => (
            <button
              key={action.title}
              type="button"
              className="opsGhostBtn"
              onClick={() => navigate(action.path)}
            >
              {action.title}
            </button>
          ))}
          <button type="button" className="opsGhostBtn" onClick={() => setEditModalOpen(true)}>
            <FontAwesomeIcon icon={faPenToSquare} /> Edit details
          </button>
          <button type="button" className="opsGhostBtn" onClick={handleDeleteVendor} disabled={deleting}>
            <FontAwesomeIcon icon={faTrash} /> {deleting ? 'Deleting...' : 'Delete vendor'}
          </button>
        </div>
      </div>

      <section className="appsTableCard vendorProfileInfoCard">
        <div className="appsTableHeader">
          <h6>Vendor profile</h6>
          <span>{vendor.vendorServices || 'Service not set'}</span>
        </div>
        <div className="vendorProfileInfoGrid">
          {infoCards.map((item) => (
            <article key={item.label} className="vendorProfileInfoItem">
              <span>
                <FontAwesomeIcon icon={item.icon} /> {item.label}
              </span>
              <strong>{item.value}</strong>
            </article>
          ))}
        </div>
      </section>

      <section className="appsTableCard vendorProfileInfoCard">
        <div className="appsTableHeader">
          <h6>Payroll and compliance</h6>
          <span>Payment method and settlement readiness</span>
        </div>
        <div className="vendorProfileInfoGrid">
          <article className="vendorProfileInfoItem">
            <span>
              <FontAwesomeIcon icon={faMoneyCheckDollar} /> Payment method
            </span>
            <strong>{payoutSummary.method || 'Not set'}</strong>
          </article>
          <article className="vendorProfileInfoItem">
            <span>
              <FontAwesomeIcon icon={faShieldHalved} /> Payout details
            </span>
            <strong>{payoutSummary.detail || 'Not set'}</strong>
          </article>
          <article className="vendorProfileInfoItem">
            <span>
              <FontAwesomeIcon icon={faIdCard} /> Payment notes
            </span>
            <strong>{vendor.paymentDetails || 'None added yet'}</strong>
          </article>
          <article className="vendorProfileInfoItem">
            <span>
              <FontAwesomeIcon icon={faCalendarDay} /> Record created
            </span>
            <strong>{formatDateTime(vendor.timeCreated, 'Not set')}</strong>
          </article>
        </div>
      </section>

      <Modal show={editModalOpen} onHide={() => !saving && setEditModalOpen(false)} centered size="lg">
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Edit vendor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="vendorEditGrid">
            <Form.Group className="mb-3">
              <Form.Label>Campus</Form.Label>
              <Form.Control
                name="branch"
                value={formState.branch}
                onChange={handleFormChange}
                placeholder="General"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select name="vendorStatus" value={formState.vendorStatus} onChange={handleFormChange}>
                {VENDOR_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Vendor name</Form.Label>
              <Form.Control name="vendorName" value={formState.vendorName} onChange={handleFormChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Contact person</Form.Label>
              <Form.Control name="vendorContactPerson" value={formState.vendorContactPerson} onChange={handleFormChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Service line</Form.Label>
              <Form.Control name="vendorServices" value={formState.vendorServices} onChange={handleFormChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Start date</Form.Label>
              <Form.Control type="datetime-local" name="commDate" value={formState.commDate} onChange={handleFormChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control name="vendorEmail" value={formState.vendorEmail} onChange={handleFormChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control name="vendorPhone" value={formState.vendorPhone} onChange={handleFormChange} />
            </Form.Group>
            <Form.Group className="mb-3 vendorEditWide">
              <Form.Label>Service description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="serviceDesc"
                value={formState.serviceDesc}
                onChange={handleFormChange}
              />
            </Form.Group>
            <Form.Group className="mb-3 vendorEditWide">
              <Form.Label>Payment method</Form.Label>
              <Form.Select
                name="vendorPaymentMethod"
                value={formState.vendorPaymentMethod}
                onChange={handleFormChange}
                disabled={vendorPaymentDetailsLocked}
              >
                {PAYMENT_METHOD_OPTIONS.map((method) => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </Form.Select>
            </Form.Group>
            {formState.vendorPaymentMethod === 'Bank Transfer' && (
              <Form.Group className="mb-3 vendorEditWide">
                <Form.Label>Bank name</Form.Label>
                <Form.Select
                  value={formState.vendorPaymentBankId}
                  onChange={handleBankSelect}
                  disabled={loadingBanks || vendorPaymentDetailsLocked}
                >
                  <option value="">{loadingBanks ? 'Loading banks...' : 'Select bank from Lotus'}</option>
                  {bankOptions.map((bank) => (
                    <option key={bank.id} value={bank.id}>
                      {bank.name}{bank.code ? ` • ${bank.code}` : ''}
                    </option>
                  ))}
                </Form.Select>
                {bankLoadError && <small className="text-danger d-block mt-2">{bankLoadError}</small>}
              </Form.Group>
            )}
            <Form.Group className="mb-3 vendorEditWide">
              <Form.Label>
                {formState.vendorPaymentMethod === 'Bank Transfer'
                  ? 'Bank account number'
                  : formState.vendorPaymentMethod === 'MPESA'
                    ? 'MPESA phone number'
                    : formState.vendorPaymentMethod === 'Paybill'
                      ? 'Paybill details'
                      : formState.vendorPaymentMethod === 'Till Number'
                        ? 'Till number'
                    : 'Payout reference'}
              </Form.Label>
              {formState.vendorPaymentMethod === 'Paybill' ? (
                <div className="vendorPaybillFields">
                  <Form.Control
                    className="mb-2"
                    name="vendorPaymentPaybillNumber"
                    value={formState.vendorPaymentPaybillNumber}
                    onChange={handleFormChange}
                    readOnly={vendorPaymentDetailsLocked}
                    placeholder="Paybill number"
                  />
                  <Form.Control
                    name="vendorPaymentPaybillAccountNumber"
                    value={formState.vendorPaymentPaybillAccountNumber}
                    onChange={handleFormChange}
                    readOnly={vendorPaymentDetailsLocked}
                    placeholder="Paybill account number"
                  />
                </div>
              ) : formState.vendorPaymentMethod === 'Till Number' ? (
                <Form.Control
                  name="vendorPaymentTillNumber"
                  value={formState.vendorPaymentTillNumber}
                  onChange={handleFormChange}
                  readOnly={vendorPaymentDetailsLocked}
                  placeholder="Till number"
                />
              ) : (
                <Form.Control
                  name={formState.vendorPaymentMethod === 'MPESA' ? 'vendorPaymentPhone' : 'vendorPaymentReference'}
                  value={
                    formState.vendorPaymentMethod === 'MPESA'
                      ? formState.vendorPaymentPhone
                      : formState.vendorPaymentReference
                  }
                  onChange={handleFormChange}
                  readOnly={vendorPaymentDetailsLocked}
                />
              )}
            </Form.Group>
            <Form.Group className="mb-0 vendorEditWide">
              <Form.Label>Payment notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="paymentDetails"
                value={formState.paymentDetails}
                onChange={handleFormChange}
                readOnly={vendorPaymentDetailsLocked}
              />
            </Form.Group>
          </div>
          <p className="mt-3 mb-0 text-muted">
            {vendorPaymentDetailsLocked
              ? 'Payment method and payout details are locked after the first save and can only be changed from Firebase.'
              : 'Save the payment method and payout details once to lock them for security.'}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-dark" onClick={() => setEditModalOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="dark" onClick={handleSaveVendor} disabled={saving}>
            {saving ? (
              <>
                <Spinner as="span" animation="border" size="sm" /> Saving...
              </>
            ) : (
              'Save vendor'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ViewVendorComp;
